import { refuseUnported } from '../janus-engine/unported';

// This is a user-requested debug snapshot, never an action/undo snapshot. postMessage
// takes the sole snapshot of the held array; formatting runs in a disposable worker.
const SERIALIZE = `self.onmessage = ({ data }) => {
    try {
        const started = performance.now();
        const json = JSON.stringify(data, null, 4);
        if (json === undefined) throw new Error('empty array');
        const serialized = performance.now();
        const blob = new Blob([json], { type: 'application/json' });
        self.postMessage({ blob, serializeMs: serialized - started, blobMs: performance.now() - serialized });
    } catch { self.postMessage({ error: 'serialize-failed' }); }
};`;

class ArrayDownloadError extends Error {}

/** Preserve the complete GM-held encounter_arr, including its computed cache. No load/adoption. */
export async function downloadHeldEncounterArray(button: HTMLButtonElement, encounterId: string): Promise<boolean> {
    if (button.disabled) return false; // the in-flight click owns progress and its eventual outcome
    const status = button.querySelector<HTMLElement>('[data-ft-array-download-status]');
    const show = (text: string): void => { if (status) status.textContent = text; };
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    show('Preparing array…');
    const started = performance.now();
    let cloneMs = 0, serializeMs = 0, blobMs = 0;
    let worker: Worker | undefined, workerUrl: string | undefined;
    try {
        const owner = window as unknown as { FateTide?: { encounterCache?: { get(id: string): unknown } } };
        const held = owner.FateTide?.encounterCache?.get(encounterId);
        if (!held) throw new ArrayDownloadError('not-held');
        // Let the busy state paint before structured cloning starts on the main thread.
        await new Promise<void>(resolve => {
            // Background tabs may park animation callbacks; exporting must still settle.
            const timer = window.setTimeout(resolve, 200);
            window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
                window.clearTimeout(timer);
                resolve();
            }));
        });
        workerUrl = URL.createObjectURL(new Blob([SERIALIZE], { type: 'text/javascript' }));
        try { worker = new Worker(workerUrl); }
        catch { throw new ArrayDownloadError('worker-unavailable'); }
        const activeWorker = worker;
        const blob = await new Promise<Blob>((resolve, reject) => {
            const timer = window.setTimeout(() => reject(new ArrayDownloadError('serialize-timeout')), 30_000);
            const finish = (result?: Blob, reason?: string): void => {
                window.clearTimeout(timer);
                if (result) resolve(result); else reject(new ArrayDownloadError(reason ?? 'serialize-failed'));
            };
            activeWorker.onmessage = (event: MessageEvent<{ blob?: Blob; serializeMs?: number; blobMs?: number; error?: string }>) => {
                serializeMs = event.data.serializeMs ?? 0;
                blobMs = event.data.blobMs ?? 0;
                if (event.data.blob instanceof Blob) finish(event.data.blob);
                else finish(undefined, 'serialize-failed');
            };
            activeWorker.onerror = () => finish(undefined, 'worker-failed');
            activeWorker.onmessageerror = () => finish(undefined, 'worker-message-failed');
            try {
                const cloneStarted = performance.now();
                activeWorker.postMessage(held);
                cloneMs = performance.now() - cloneStarted;
            }
            catch { finish(undefined, 'snapshot-failed'); }
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        try {
            link.href = url;
            link.download = `encounter-arr-${encounterId}.json`;
            document.body.appendChild(link);
            link.click();
        } finally {
            link.remove();
            // The browser consumes the URL asynchronously after the click dispatch.
            window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
        }
        show('Array downloaded');
        window.dispatchEvent(new CustomEvent('ft:encounter-array-downloaded', { detail: {
            encounterId, bytes: blob.size, cloneMs, serializeMs, blobMs, elapsedMs: performance.now() - started,
        } }));
        return true;
    } catch (error) {
        const reason = `encounter-array:${error instanceof ArrayDownloadError ? error.message : 'download-failed'}`;
        const message = `Could not download the encounter array (${reason}). Try again.`;
        show(message);
        refuseUnported(reason, { encounter_id: encounterId }, message);
        return false;
    } finally {
        worker?.terminate();
        if (workerUrl) URL.revokeObjectURL(workerUrl);
        button.disabled = false;
        button.removeAttribute('aria-busy');
    }
}
